// src/components/AuthScreen.jsx
import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useUser, API_BASE } from "../context/UserContext";

const C = {
  primary:    "#655d54",
  primaryDim: "#595149",
  bg:         "#faf9f6",
  surface:    "#ffffff",
  container:  "#f4f4f0",
  onSurface:  "#2f3430",
  variant:    "#5c605c",
  outline:    "#8a8e8a",
  error:      "#9e422c",
};

const FH = "'Noto Serif', Georgia, serif";
const FL = "'Manrope', -apple-system, sans-serif";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export default function AuthScreen({ embedded = false }) {
  const { t } = useTranslation();
  const { login, register, googleLogin } = useUser();
  const [mode, setMode] = useState("login");
  const [nickname, setNickname] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [nickStatus, setNickStatus] = useState(null); // null | "checking" | "available" | "taken"
  const nickCheckTimer = useRef(null);
  const googleBtnRef = useRef(null);
  const gsiInitialized = useRef(false);

  const checkNickname = useCallback((nick) => {
    clearTimeout(nickCheckTimer.current);
    if (nick.trim().length < 2) { setNickStatus(null); return; }
    setNickStatus("checking");
    nickCheckTimer.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_BASE}/users/check-nickname?nickname=${encodeURIComponent(nick.trim())}`);
        setNickStatus(res.data.available ? "available" : "taken");
      } catch { setNickStatus(null); }
    }, 400);
  }, []);

  // Initialize Google Identity Services
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || gsiInitialized.current) return;

    const initGSI = () => {
      if (!window.google?.accounts?.id || !googleBtnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response) => {
          setError("");
          setLoading(true);
          try {
            await googleLogin(response.credential);
          } catch (e) {
            setError(e.response?.data?.detail || t("auth.google_failed"));
          } finally {
            setLoading(false);
          }
        },
      });
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        type: "standard",
        theme: "outline",
        size: "large",
        width: googleBtnRef.current.offsetWidth,
        text: "continue_with",
        shape: "pill",
      });
      gsiInitialized.current = true;
    };

    // Load GSI script if not already loaded
    if (window.google?.accounts?.id) {
      initGSI();
    } else if (!document.querySelector('script[src*="accounts.google.com/gsi/client"]')) {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.onload = initGSI;
      document.head.appendChild(script);
    }
  }, []); // eslint-disable-line

  const handleSubmit = async () => {
    setError("");
    if (nickname.trim().length < 2) { setError(t("auth.nickname_min")); return; }
    if (mode === "register" && nickStatus === "taken") { setError(t("auth.nickname_taken")); return; }
    if (!/^\d{6}$/.test(pin)) { setError(t("auth.pin_invalid")); return; }
    setLoading(true);
    try {
      if (mode === "login") await login(nickname.trim(), pin);
      else await register(nickname.trim(), pin);
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (mode === "login" && e.response?.status === 401) {
        setError(t("auth.login_failed"));
      } else {
        setError(detail || t("auth.try_again"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      ...(embedded ? {} : { position: "fixed", inset: 0, zIndex: 1000 }),
      background: C.bg,
      display: "flex",
      fontFamily: FL,
    }}>
      {/* Left — Visual panel (desktop only, full-screen mode) */}
      {!embedded && <div style={{
        display: "none",
        width: "50%", position: "relative", overflow: "hidden",
        background: `linear-gradient(160deg, ${C.primaryDim} 0%, ${C.primary} 60%, #877a6f 100%)`,
      }}
        className="auth-left"
      >
        {/* Pattern overlay */}
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: `radial-gradient(circle at 30% 70%, rgba(255,246,239,0.08) 0%, transparent 60%),
                            radial-gradient(circle at 80% 20%, rgba(255,246,239,0.06) 0%, transparent 50%)`,
        }} />
        {/* Text */}
        <div style={{ position: "absolute", bottom: 48, left: 48, zIndex: 10 }}>
          <h1 style={{
            fontFamily: FH, fontStyle: "italic",
            fontSize: 40, fontWeight: 700, color: "#fff6ef",
            margin: "0 0 8px", letterSpacing: "-0.5px",
          }}>
            {t("app.name")}
          </h1>
          <p style={{
            fontFamily: FL, fontSize: 11,
            textTransform: "uppercase", letterSpacing: "0.3em",
            color: "rgba(255,246,239,0.6)", margin: 0,
          }}>
            {t("app.tagline")}
          </p>
        </div>
        {/* Decorative circles */}
        <div style={{
          position: "absolute", top: -80, right: -80,
          width: 300, height: 300, borderRadius: "50%",
          border: "1px solid rgba(255,246,239,0.1)",
        }} />
        <div style={{
          position: "absolute", top: 40, right: 40,
          width: 180, height: 180, borderRadius: "50%",
          border: "1px solid rgba(255,246,239,0.08)",
        }} />
      </div>}

      {/* Right — Input panel */}
      <div style={{
        flex: 1, display: "flex", flexDirection: "column",
        alignItems: "center",
        padding: embedded ? "24px 20px" : "40px 24px",
        background: C.bg,
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
      }}>
        <div style={{ width: "100%", maxWidth: 400, margin: "auto 0" }}>

          {/* Brand */}
          <div style={{ marginBottom: 12 }}>
            <h1 style={{
              fontFamily: FH, fontStyle: "italic",
              fontSize: 28, fontWeight: 700, color: C.primary,
              margin: 0,
            }}>
              {t("app.name")}
            </h1>
            <div style={{
              width: 32, height: 2,
              background: C.outline, marginTop: 12, opacity: 0.4,
            }} />
          </div>

          {/* Mode tabs */}
          <div style={{
            display: "flex", gap: 0, marginBottom: 28,
            background: C.container, borderRadius: 10, padding: 3,
          }}>
            {[
              { key: "login", label: t("auth.sign_in") },
              { key: "register", label: t("auth.sign_up") },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => { setMode(tab.key); setError(""); setNickStatus(null); }}
                style={{
                  flex: 1, padding: "10px 0",
                  background: mode === tab.key ? C.surface : "transparent",
                  border: "none", borderRadius: 8,
                  fontFamily: FL, fontSize: 13, fontWeight: mode === tab.key ? 700 : 500,
                  color: mode === tab.key ? C.onSurface : C.outline,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  boxShadow: mode === tab.key ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Header */}
          <div style={{ marginBottom: 28 }}>
            <h2 style={{
              fontFamily: FH, fontSize: 28, fontWeight: 700,
              color: C.onSurface, margin: "0 0 6px", letterSpacing: "-0.5px",
            }}>
              {mode === "login" ? t("auth.welcome_back") : t("auth.nice_to_meet")}
            </h2>
            <p style={{
              fontFamily: FL, fontSize: 13,
              color: C.variant, lineHeight: 1.6, margin: 0,
            }}>
              {mode === "login"
                ? t("auth.sign_in_desc")
                : t("auth.sign_up_desc")}
            </p>
          </div>

          {/* Input fields */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20, marginBottom: 28 }}>
            <div>
              <Field
                label={t("auth.nickname")}
                value={nickname}
                onChange={(e) => { setNickname(e.target.value); if (mode === "register") checkNickname(e.target.value); }}
                placeholder={t("auth.nickname_placeholder")}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              />
              {mode === "register" && nickname.trim().length >= 2 && nickStatus && (
                <p style={{
                  margin: "6px 0 0 2px", fontFamily: FL, fontSize: 11, fontWeight: 600,
                  color: nickStatus === "available" ? "#1D9E75" : nickStatus === "taken" ? C.error : C.outline,
                }}>
                  {nickStatus === "checking" ? t("auth.checking") : nickStatus === "available" ? t("auth.available") : t("auth.taken")}
                </p>
              )}
            </div>
            <Field
              label={t("auth.pin")}
              type="password"
              inputMode="numeric"
              maxLength={6}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder={mode === "register" ? "000000" : "000000"}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              style={{ textAlign: "center", letterSpacing: 10, fontSize: 18 }}
            />
          </div>


          {/* Error */}
          {error && (
            <p style={{
              fontFamily: FL, fontSize: 12,
              color: C.error, marginBottom: 16,
              padding: "10px 14px", background: "#fef0ec",
              borderRadius: 8, margin: "0 0 20px",
            }}>
              {error}
            </p>
          )}

          {/* Submit button */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              width: "100%", padding: "16px",
              background: loading ? C.outline : C.primary,
              color: "#fff6ef", border: "none",
              borderRadius: 12,
              fontFamily: FL, fontSize: 14, fontWeight: 700,
              cursor: loading ? "not-allowed" : "pointer",
              letterSpacing: "0.02em",
              transition: "background 0.2s, transform 0.1s",
              boxShadow: loading ? "none" : "0 4px 16px rgba(101,93,84,0.2)",
            }}
            onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = C.primaryDim; }}
            onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = C.primary; }}
          >
            {loading ? t("auth.loading") : mode === "login" ? t("auth.sign_in") : t("auth.create_account")}
          </button>

          {/* Google login */}
          {GOOGLE_CLIENT_ID && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 14, margin: "24px 0" }}>
                <div style={{ flex: 1, height: 1, background: `${C.outline}33` }} />
                <span style={{ fontFamily: FL, fontSize: 11, color: C.outline }}>{t("auth.or")}</span>
                <div style={{ flex: 1, height: 1, background: `${C.outline}33` }} />
              </div>
              <div ref={googleBtnRef} style={{ width: "100%", paddingBottom: 16 }} />
            </>
          )}

        </div>
      </div>

      <style>{`
        @media (min-width: 768px) {
          .auth-left { display: block !important; }
        }
      `}</style>
    </div>
  );
}

function Field({ label, style: extraStyle = {}, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <label style={{
        display: "block", fontFamily: FL,
        fontSize: 10, fontWeight: 600,
        textTransform: "uppercase", letterSpacing: "0.15em",
        color: "#5c605c", marginBottom: 8,
      }}>
        {label}
      </label>
      <input
        {...props}
        onFocus={(e) => { setFocused(true); props.onFocus?.(e); }}
        onBlur={(e) => { setFocused(false); props.onBlur?.(e); }}
        style={{
          width: "100%", padding: "14px 16px",
          background: "#f4f4f0", border: "none",
          borderRadius: 12,
          outline: focused ? `2px solid #655d54` : "2px solid transparent",
          fontFamily: FL,
          fontSize: 15, color: "#2f3430",
          boxSizing: "border-box",
          transition: "outline 0.15s",
          WebkitAppearance: "none",
          ...extraStyle,
        }}
      />
    </div>
  );
}
